import { useState, useEffect } from 'react';
import * as React from 'react';
import { FaStar, FaRegStar, FaPhoneAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { colors, shadows } from '../../employees/styles/personalakteStyles';
import InterviewService from '../services/InterviewService';
import type {
  Applicant,
  InterviewFormData,
  PhoneInterview,
  PhoneInterviewSubmitData,
  InterviewRecommendation,
} from '../types';

interface PhoneInterviewFormProps {
  applicant: Applicant;
  onClose: () => void;
  onSaved: () => void;
}

const RECOMMENDATION_OPTIONS: { value: InterviewRecommendation; label: string; color: string }[] = [
  { value: 'HIRE', label: 'Einstellen', color: '#22c55e' },
  { value: 'TRIAL', label: 'Probearbeiten', color: '#f59e0b' },
  { value: 'REJECT', label: 'Ablehnen', color: '#ef4444' },
  { value: 'UNDECIDED', label: 'Unentschieden', color: '#94a3b8' },
];

export default function PhoneInterviewForm({ applicant, onClose, onSaved }: PhoneInterviewFormProps): React.JSX.Element {
  const [formData, setFormData] = useState<InterviewFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [criteriaResponses, setCriteriaResponses] = useState<Record<string, any>>({});
  const [standardResponses, setStandardResponses] = useState<Record<string, any>>({});
  const [customResponses, setCustomResponses] = useState<Record<string, any>>({});
  const [meetsMinimum, setMeetsMinimum] = useState(true);
  const [rating, setRating] = useState<1 | 2 | 3 | 4 | 5>(3);
  const [recommendation, setRecommendation] = useState<InterviewRecommendation>('UNDECIDED');
  const [notes, setNotes] = useState('');

  const [pastInterviews, setPastInterviews] = useState<PhoneInterview[]>([]);

  useEffect(() => {
    loadForm();
    loadPastInterviews();
  }, [applicant.id]);

  const loadForm = async () => {
    try {
      setLoading(true);
      const data = await InterviewService.getInterviewForm(applicant.id);
      setFormData(data);
    } catch (e: any) {
      console.error('[PhoneInterviewForm] load error:', e);
      toast.error('Interview-Formular konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const loadPastInterviews = async () => {
    try {
      const interviews = await InterviewService.fetchInterviews(applicant.id);
      setPastInterviews(interviews);
    } catch {
      // silent
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const data: PhoneInterviewSubmitData = {
        criteria_responses: criteriaResponses,
        standard_responses: standardResponses,
        custom_responses: customResponses,
        meets_minimum_criteria: meetsMinimum,
        overall_rating: rating,
        recommendation,
        notes: notes || undefined,
      };
      await InterviewService.submitInterview(applicant.id, data);
      toast.success('Interview gespeichert.');
      onSaved();
    } catch (e: any) {
      toast.error('Interview konnte nicht gespeichert werden.');
      console.error('[PhoneInterviewForm] submit error:', e);
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 8,
    border: `1px solid ${colors.border}`, fontSize: 13, outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 4,
  };

  if (loading) {
    return (
      <ModalShell onClose={onClose}>
        <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted, fontSize: 13 }}>
          Lade Interview-Formular...
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={onClose}>
      <div style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', padding: '0 4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <FaPhoneAlt style={{ color: colors.slate, fontSize: 16 }} />
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.textPrimary }}>
            Telefoninterview — {applicant.first_name} {applicant.last_name}
          </h3>
        </div>

        {formData?.template && (
          <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 16, padding: '6px 10px', background: colors.bgMuted, borderRadius: 8 }}>
            Template: <strong>{formData.template.name}</strong>
            {formData.template.team && ` | Team: ${formData.template.team}`}
            {formData.template.employment_type && ` | Typ: ${formData.template.employment_type}`}
          </div>
        )}

        {/* Criteria from template */}
        {formData?.template?.criteria && formData.template.criteria.length > 0 && (
          <Section title="Kriterien">
            {formData.template.criteria.map(criterion => (
              <div key={criterion.key} style={{ marginBottom: 12 }}>
                <label style={labelStyle}>
                  {criterion.label}
                  {criterion.minimum != null && (
                    <span style={{ fontWeight: 400, color: colors.textLight }}> (Minimum: {criterion.minimum})</span>
                  )}
                  {criterion.required && <span style={{ color: colors.danger }}> *</span>}
                </label>
                {criterion.type === 'select' && criterion.options ? (
                  <select
                    value={criteriaResponses[criterion.key] || ''}
                    onChange={e => setCriteriaResponses(prev => ({ ...prev, [criterion.key]: e.target.value }))}
                    style={inputStyle}
                  >
                    <option value="">— Bitte wählen —</option>
                    {criterion.options.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : criterion.type === 'number' ? (
                  <input
                    type="number"
                    value={criteriaResponses[criterion.key] ?? ''}
                    onChange={e => setCriteriaResponses(prev => ({ ...prev, [criterion.key]: e.target.value ? Number(e.target.value) : '' }))}
                    style={inputStyle}
                  />
                ) : (
                  <input
                    type="text"
                    value={criteriaResponses[criterion.key] || ''}
                    onChange={e => setCriteriaResponses(prev => ({ ...prev, [criterion.key]: e.target.value }))}
                    style={inputStyle}
                  />
                )}
              </div>
            ))}
          </Section>
        )}

        {/* Standard questions */}
        <Section title="Standardfragen">
          {[
            { key: 'weekday_availability', label: 'Verfügbarkeit Wochentage (welche Tage?)' },
            { key: 'weekend_availability', label: 'Verfügbarkeit Wochenende?' },
            { key: 'available_hours', label: 'Von wann bis wann verfügbar?' },
            { key: 'planned_travel', label: 'Reisen geplant?' },
            { key: 'other_absences', label: 'Andere anstehende Abwesenheiten?' },
            { key: 'earliest_start', label: 'Frühester Starttermin' },
          ].map(q => (
            <div key={q.key} style={{ marginBottom: 12 }}>
              <label style={labelStyle}>{q.label}</label>
              <input
                type="text"
                value={standardResponses[q.key] || ''}
                onChange={e => setStandardResponses(prev => ({ ...prev, [q.key]: e.target.value }))}
                style={inputStyle}
              />
            </div>
          ))}
        </Section>

        {/* Custom questions from template */}
        {formData?.template?.custom_questions && formData.template.custom_questions.length > 0 && (
          <Section title="Zusatzfragen">
            {formData.template.custom_questions.map(q => (
              <div key={q.key} style={{ marginBottom: 12 }}>
                <label style={labelStyle}>{q.label}</label>
                {q.type === 'textarea' ? (
                  <textarea
                    value={customResponses[q.key] || ''}
                    onChange={e => setCustomResponses(prev => ({ ...prev, [q.key]: e.target.value }))}
                    rows={2}
                    style={{ ...inputStyle, resize: 'vertical' }}
                  />
                ) : q.type === 'select' && q.options ? (
                  <select
                    value={customResponses[q.key] || ''}
                    onChange={e => setCustomResponses(prev => ({ ...prev, [q.key]: e.target.value }))}
                    style={inputStyle}
                  >
                    <option value="">— Bitte wählen —</option>
                    {q.options.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={customResponses[q.key] || ''}
                    onChange={e => setCustomResponses(prev => ({ ...prev, [q.key]: e.target.value }))}
                    style={inputStyle}
                  />
                )}
              </div>
            ))}
          </Section>
        )}

        {/* Rating & Recommendation */}
        <Section title="Bewertung">
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Mindestkriterien erfüllt?</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[true, false].map(val => (
                <button
                  key={String(val)}
                  onClick={() => setMeetsMinimum(val)}
                  style={{
                    padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    border: meetsMinimum === val ? 'none' : `1px solid ${colors.border}`,
                    background: meetsMinimum === val ? (val ? colors.successLight : colors.dangerLight) : colors.bgCard,
                    color: meetsMinimum === val ? (val ? '#166534' : '#991b1b') : colors.textBody,
                  }}
                >
                  {val ? 'Ja' : 'Nein'}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Gesamtbewertung</label>
            <div style={{ display: 'flex', gap: 4 }}>
              {([1, 2, 3, 4, 5] as const).map(star => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, fontSize: 20 }}
                >
                  {star <= rating
                    ? <FaStar color="#f59e0b" />
                    : <FaRegStar color={colors.border} />
                  }
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Empfehlung</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {RECOMMENDATION_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setRecommendation(opt.value)}
                  style={{
                    padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: recommendation === opt.value ? 'none' : `1px solid ${colors.border}`,
                    background: recommendation === opt.value ? opt.color : colors.bgCard,
                    color: recommendation === opt.value ? 'white' : colors.textBody,
                    transition: 'all 0.15s',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={labelStyle}>Notizen</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder="Freitext-Notizen zum Interview..."
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
        </Section>

        {/* Past interviews */}
        {pastInterviews.length > 0 && (
          <Section title={`Frühere Interviews (${pastInterviews.length})`}>
            {pastInterviews.map(interview => (
              <div key={interview.id} style={{
                padding: 12, borderRadius: 10, background: colors.bgMuted,
                marginBottom: 8, fontSize: 12,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontWeight: 600, color: colors.textBody }}>
                    {new Date(interview.conducted_at).toLocaleDateString('de-DE')}
                    {interview.interviewer_name && ` — ${interview.interviewer_name}`}
                  </span>
                  <span style={{
                    padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600,
                    background: RECOMMENDATION_OPTIONS.find(o => o.value === interview.recommendation)?.color || colors.textMuted,
                    color: 'white',
                  }}>
                    {RECOMMENDATION_OPTIONS.find(o => o.value === interview.recommendation)?.label || interview.recommendation}
                  </span>
                </div>
                <div style={{ color: colors.textMuted }}>
                  Rating: {'★'.repeat(interview.overall_rating)}{'☆'.repeat(5 - interview.overall_rating)}
                  {' | '}Mindestkriterien: {interview.meets_minimum_criteria ? 'Ja' : 'Nein'}
                </div>
                {interview.notes && (
                  <div style={{ marginTop: 6, color: colors.textBody, fontStyle: 'italic' }}>
                    {interview.notes}
                  </div>
                )}
              </div>
            ))}
          </Section>
        )}

        {/* Submit */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20, paddingBottom: 4 }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px', borderRadius: 8, border: `1px solid ${colors.border}`,
              background: colors.bgCard, cursor: 'pointer', fontSize: 12, fontWeight: 500,
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: 'linear-gradient(135deg, #5271A5, #3b5998)', color: 'white',
              cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600,
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Speichern...' : 'Interview speichern'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

/* ---- Helpers ---- */

function ModalShell({ children, onClose }: { children: React.ReactNode; onClose: () => void }): React.JSX.Element {
  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(14, 19, 51, 0.4)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: colors.bgCard, borderRadius: 16, padding: 28, width: 560,
        boxShadow: shadows.elevated, maxHeight: '90vh', overflow: 'hidden',
      }}>
        {children}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ marginBottom: 20 }}>
      <h4 style={{
        margin: '0 0 12px', fontSize: 13, fontWeight: 600, color: colors.textPrimary,
        borderBottom: `1px solid ${colors.borderLight}`, paddingBottom: 6,
      }}>
        {title}
      </h4>
      {children}
    </div>
  );
}
